import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MaskTitle, Reveal, SectionLabel } from "./motion";
import { formatDate } from "@/lib/race";
import { localized } from "@/lib/i18n";
import { useLang } from "@/context/LangContext";

const Card = ({ n, i, big, onOpen }) => {
  const { t, lang } = useLang();
  return (
  <button onClick={onOpen} data-testid={`news-card-${i}`} className="group flex h-full w-full flex-col text-left">
    <div className={`relative w-full overflow-hidden bg-surface ${big ? "aspect-[4/3] md:aspect-[16/11]" : "aspect-[4/3]"}`}>
      {n?.image && <img src={n.image} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />}
      <span className="label-mono absolute left-4 top-4 bg-ink/80 px-3 py-1 text-[11px] text-volt backdrop-blur">{n?.tag}</span>
      <span className="absolute bottom-4 right-4 grid h-11 w-11 place-items-center rounded-full bg-trident text-white transition-transform duration-500 group-hover:rotate-45">
        <ArrowUpRight className="h-5 w-5" />
      </span>
    </div>
    <p className="label-mono mt-5 text-white">{formatDate(n?.date, t.months)}</p>
    <h3 className={`mt-2 font-display font-extrabold uppercase leading-[0.95] text-white transition-colors group-hover:text-volt ${big ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"}`}>{localized(n, "title", lang)}</h3>
    {n?.excerpt && <p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-400 md:text-base">{localized(n, "excerpt", lang)}</p>}
  </button>
  );
};

export const News = ({ news }) => {
  const list = (Array.isArray(news) ? news : []).slice(0, 3);
  const [open, setOpen] = useState(null);
  const { t, lang } = useLang();
  if (list.length === 0) return null;
  const [first, ...rest] = list;

  return (
    <section id="news" data-testid="news-section" className="bg-ink px-5 pb-24 md:px-10 md:pb-36">
      <div className="mx-auto max-w-[1600px] border-t border-white/10 pt-24 md:pt-36">
        <SectionLabel index="04">{t.news.label}</SectionLabel>
        <MaskTitle key={lang} lines={t.news.title} className="mt-6 font-display text-6xl font-black uppercase leading-[0.85] md:text-8xl lg:text-9xl" />
        <div className="mt-14 grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-8">
          <Reveal className="md:col-span-7"><Card n={first} i={0} big onOpen={() => setOpen(first)} /></Reveal>
          <div className="flex flex-col gap-12 md:col-span-5">
            {rest.map((n, i) => (
              <Reveal key={n?.id || i} delay={0.1 * (i + 1)}><Card n={n} i={i + 1} onOpen={() => setOpen(n)} /></Reveal>
            ))}
          </div>
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-lenis-prevent data-testid="news-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-surface p-0">
          {open?.image && <img src={open.image} alt="" className="aspect-[16/9] w-full object-cover" />}
          <div className="p-6 md:p-8">
            <p className="label-mono text-volt">{open?.tag} · {formatDate(open?.date, t.months)}</p>
            <DialogTitle className="mt-3 font-display text-4xl font-black uppercase leading-none">{localized(open, "title", lang)}</DialogTitle>
            <DialogDescription className="mt-4 whitespace-pre-line text-base leading-relaxed text-zinc-300">{localized(open, "body", lang) || localized(open, "excerpt", lang)}</DialogDescription>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
